import type { JSX } from 'react'
import type { Analysis } from '../../api/types'
import { ANALYSIS_STAGES, stageIndexOf, stageLabel } from '../../analysis/draft'

export type AnalysisProgressProps = {
  analysis: Analysis
  cancelling: boolean
  onCancel: () => void
}

type StageState = 'done' | 'current' | 'pending'

const MARKERS: Record<StageState, string> = { done: '✓', current: '▸', pending: '·' }

/** Индекс текущего этапа: сперва по идентификатору, потом по номеру от сервиса. */
function currentStageIndex(analysis: Analysis): number {
  const known = stageIndexOf(analysis.stage)
  if (known >= 0) return known
  const byNumber = analysis.stageIndex - 1
  if (Number.isFinite(byNumber) && byNumber >= 0) {
    return Math.min(ANALYSIS_STAGES.length - 1, byNumber)
  }
  return 0
}

export function AnalysisProgress({
  analysis,
  cancelling,
  onCancel,
}: AnalysisProgressProps): JSX.Element {
  const queued = analysis.status === 'queued'
  const index = queued ? -1 : currentStageIndex(analysis)
  const percent = Math.round(Math.min(1, Math.max(0, analysis.progress || 0)) * 100)
  const unknownStage = !queued && stageIndexOf(analysis.stage) < 0 && analysis.stage !== ''

  return (
    <div className="stack">
      <div className="row wrap">
        <strong>{queued ? 'В очереди' : `Этап: ${stageLabel(analysis.stage)}`}</strong>
        <span className="spacer" />
        <span className="faint mono">{percent}%</span>
      </div>

      <div className="progress">
        <i style={{ width: `${percent}%` }} />
      </div>

      {analysis.message ? <p className="faint" style={{ margin: 0 }}>{analysis.message}</p> : null}

      <ol className="analysis-stages">
        {ANALYSIS_STAGES.map((stage, i) => {
          const state: StageState = queued ? 'pending' : i < index ? 'done' : i === index ? 'current' : 'pending'
          return (
            <li key={stage.id} className={`analysis-stage ${state}`}>
              <span className="analysis-stage-mark">{MARKERS[state]}</span>
              <span className="analysis-stage-label">{stage.label}</span>
              <span className="faint analysis-stage-hint">{stage.hint}</span>
            </li>
          )
        })}
      </ol>

      {unknownStage ? (
        <p className="faint" style={{ margin: 0 }}>
          Сервис прислал незнакомый этап «{analysis.stage}» — показываю как есть.
        </p>
      ) : null}

      <div className="row wrap">
        <button type="button" className="danger" disabled={cancelling} onClick={onCancel}>
          {cancelling ? 'Отменяю…' : 'Отменить разбор'}
        </button>
        <span className="faint">
          Отмена прерывает текущий этап, а не ждёт его конца. Ничего в проекте ещё не изменилось.
        </span>
      </div>
    </div>
  )
}
